








































































































import { getBranchesOfClient, loadClient } from "@/data/repository/ClientRepository";
import { Component, Watch } from "vue-property-decorator";
import PickerDialogComponent from "../common/PickerDialog.vue";
import ClickableTextFieldComponent from "@/components/common/ClickableTextField.vue";
import { ProjectSupervisorSource, UserSettings } from "@/data/models/Settings"
import { UserSettingsModule } from "@/store/modules/UserSettingsModule";
import { defineOwnUserSettingsClientAccess } from "@/permissions/ClientPermissions";
import { updateUserSettings } from "@/data/repository/SettingsRepository"
import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import { createDefaultUserSettings } from "@/data/models/Client";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";

@Component({
  components: {
    "clickable-text-field": ClickableTextFieldComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    picker: PickerDialogComponent,
  },
})
export default class UserSettingsComponent extends SettingsPageMixin {
  isInitializing = true;

  cockpitActiveForAllUsers = true;
  alwaysUseManagerAsSupervisor = true;
  keepProjectOwner = true;
  strictBranchFilters = false;

  yesNoValues = [true, false];
  minProgramChainDurationInMin = 0;

  initialUserSettings: UserSettings = createDefaultUserSettings();
  
  @Watch("userSettings")
  onUserSettingsChanged() {
    if (this.userSettings && !this.userSettings.areDefaultUserSettings) {
      this.initializeParameters(this.userSettings);  
    }
  }

  get disableStrictBranchFilterSelection() {
    if (this.strictBranchFilters) {
      return false;
    }

    const branchesOfClient = getBranchesOfClient();

    return branchesOfClient.length === 0;
  }

  get userSettings() {
    return UserSettingsModule.userSettings;
  }

  get hasChanged() {
    const alwaysUseManagerAsSupervisor = this.initialUserSettings.projectSupervisorSource ===
        ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER;

    return (
      this.cockpitActiveForAllUsers !== this.initialUserSettings.cockpitActiveForAllUsers ||
      this.alwaysUseManagerAsSupervisor !== alwaysUseManagerAsSupervisor ||
      this.keepProjectOwner !== this.initialUserSettings.keepProjectOwner ||
      this.strictBranchFilters !== this.initialUserSettings.strictBranchFilters ||
      this.minProgramChainDurationInMin !== this.initialUserSettings.minProgramChainDurationInMin
    );
  }

  get canEdit() {
    return defineOwnUserSettingsClientAccess();
  }

  get readonly() {
    return !this.isEditMode || !this.canEdit;
  }

  get formatYesNo() {
    return (value: boolean) => {
      return this.$t(`common.${value ? "yes" : "no"}`).toString();
    };
  }

  minProgramChainDurationValidation(input: string) {
    const value = Number(input);
    const isValid = Number.isInteger(value) && value >= 0;
    return isValid ? null : this.$t("settings.errorInvalidNumber");
  }

  initializeParameters(
    userSettings: UserSettings
  ) {
    if (userSettings.projectSupervisorSource) {
      this.alwaysUseManagerAsSupervisor =
        userSettings.projectSupervisorSource ===
        ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER;
    }

    if (userSettings.strictBranchFilters !== undefined) {
      this.strictBranchFilters = userSettings.strictBranchFilters;
    }

    if (userSettings.keepProjectOwner !== undefined) {
      this.keepProjectOwner = userSettings.keepProjectOwner;
    }

    this.minProgramChainDurationInMin =
      Math.max(0, userSettings.minProgramChainDurationInMin ?? 0);

    this.cockpitActiveForAllUsers =
      userSettings.cockpitActiveForAllUsers ?? true;

    this.initialUserSettings = {
      cockpitActiveForAllUsers: this.cockpitActiveForAllUsers,
      minProgramChainDurationInMin: this.minProgramChainDurationInMin,
      projectSupervisorSource: this.alwaysUseManagerAsSupervisor ? ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER : ProjectSupervisorSource.MANUAL_SELECTION,
      strictBranchFilters: this.strictBranchFilters,
      keepProjectOwner: this.keepProjectOwner
    }

    this.isEditMode = false;
  }

  async created() {
    this.isInitializing = true;
    const client = await loadClient();
    const userSettings = UserSettingsModule.userSettings;

    const userSettingsDeprecated = client?.userSettings;
    const cockpitSettingsDeprecated = client?.cockpitSettings;

    if (userSettings && !userSettings.areDefaultUserSettings) {
      this.initializeParameters(userSettings);
    }
    else {
      this.initializeParameters({
        projectSupervisorSource: userSettingsDeprecated?.projectSupervisorSource,
        strictBranchFilters: userSettingsDeprecated?.strictBranchFilters,
        keepProjectOwner: userSettingsDeprecated?.keepProjectOwner,
        minProgramChainDurationInMin: client?.minProgramChainDurationInMin,

        cockpitActiveForAllUsers: cockpitSettingsDeprecated?.cockpitActiveForAllUsers ?? true,
      });
    }

    this.isInitializing = false;
  }

  async onNewMinProgramChainDurationInMin(input: string) {
    const minProgramChainDurationInMin = Number(input);
    this.minProgramChainDurationInMin = Math.max(0, minProgramChainDurationInMin);
  }

  async doSave() {
    const userSettingsUpdate: UserSettings = {
      projectSupervisorSource: this.alwaysUseManagerAsSupervisor
        ? ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER
        : ProjectSupervisorSource.MANUAL_SELECTION,
      strictBranchFilters: this.strictBranchFilters,
      keepProjectOwner: this.keepProjectOwner,
      minProgramChainDurationInMin: Math.max(0, this.minProgramChainDurationInMin),

      cockpitActiveForAllUsers: this.cockpitActiveForAllUsers
    }
    await updateUserSettings(userSettingsUpdate);
  }

  mounted() {
    this.setToolbarTitle(this.$t("settings.userSettings.title").toString());
  }
}
